import { useMemo } from 'react'
import { useMedia } from 'react-use'
import { Box, BoxProps, Container } from '@mui/material'
import { useRouter } from 'next/router'

import SearchBar from '@/components/base/search-bar'
import { Media } from '@/components/sections/media'
import { useAppStore } from '@/contexts/store'
import { isHappa } from '@/hooks'
import { useLocaleStore } from '@/store/locale'

import { useCities } from '@/api/cities'
import { WPLink, WPMedia } from '@/types'

export type HeroProps = BoxProps & {
  title: string
  text: string
  button_1: {
    link: WPLink
    icon: string
  }
  button_2: {
    link: WPLink
    icon: string
  }
  media: WPMedia
  media_mobile?: WPMedia
  show_widget: boolean
  is_first?: boolean
}

const Hero = ({
  title,
  text,
  button_1,
  button_2,
  media,
  media_mobile,
  show_widget,
  is_first,
  ...props
}: HeroProps) => {
  const { strings } = useAppStore()
  const router = useRouter()
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const { collection: cities } = useCities({ per_page: 100, onlyWithAdvertisements: true })
  const isSM = useMedia('(max-width: 767px)', true)
  const isUnder1400 = useMedia('(max-width: 1399px)', true)
  const media_ = isSM && media_mobile?.image ? media_mobile || media : media
  const isTabletRange = isUnder1400 && !isSM

  const cityOptions = useMemo(
    () =>
      cities
        ?.map((city) => ({
          value: String(city.id),
          label: city.name[currentLocale as keyof typeof city.name] || city.name.it
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [cities, currentLocale]
  )

  const handleCitySearch = (cityId: string) => {
    if (!cityId) return
    router.push({
      pathname: strings?.links?.browse_ads?.url,
      query: { city_id: cityId, filter_count: true }
    })
  }

  return (
    <Box {...props}>
      <Container>
        <Media
          invertButtons={isHappa}
          sx={
            !isHappa
              ? {
                  '@media (min-width: 768px) and (max-width: 1399px)': {
                    height: '500px'
                  }
                }
              : undefined
          }
          media={media_}
          info={{
            title: title,
            text: text,
            button_1: button_1,
            button_2: button_2,
            align: isTabletRange ? 'center' : isHappa ? 'center' : 'bottom',
            is_first: is_first
          }}
        >
          {show_widget ? (
            <Box
              sx={{
                width: '100%',
                position: 'relative'
              }}
            >
              <SearchBar
                selectLabel={strings?.labels?.select_city_placeholder}
                selectPlaceholder={strings?.labels?.select}
                selectOptions={
                  isHappa
                    ? cityOptions
                    : [
                        { label: 'Singola', value: 'singola' },
                        { label: 'Doppia', value: 'doppia' },
                        { label: 'Appartamento', value: 'appartamento' }
                      ]
                }
                onSearch={isHappa ? handleCitySearch : undefined}
              />
            </Box>
          ) : null}
        </Media>
      </Container>
    </Box>
  )
}

export default Hero
